/**
 * IAB Classifier Adapter for A/B Testing
 *
 * Consolidated from:
 * - fullClassifier.ts (Sprint 11e) - Core classification with batch loop (Fix 0B)
 * - full-classifier.ts (Sprint 11d) - CostTracker integration
 * - parallel-classify.ts - Worker singleton, runSummarization
 *
 * This file is the single source of truth for IAB classification in A/B testing.
 *
 * @see docs/bugfixing/2025-01-15-classifier-file-redundancy.md
 * @see docs/sprints/ownyou-sprint11e-spec.md
 */

import {
  createIABClassifier,
  CostTracker,
  RouteBackedAnalyzerLLMClient,
  type IABRouteClientFactory,
  type TaxonomySelection,
} from '@ownyou/iab-classifier';
import { configService } from '@ownyou/llm-client';
import type { LangGraphStoreAdapter } from '../store/LangGraphStoreAdapter';
import type { BaseStore } from '@langchain/langgraph/web';
import {
  IAB_CLASSIFICATION_ROUTE_IDS,
  IAB_EVIDENCE_JUDGE_ROUTE_ID,
  IAB_INPUT_PREPROCESSING_ROUTE_ID,
  executeLlmTextRoute,
  isLlmRouteCloudProvider,
  type LlmRouteTelemetryEvent,
  type LlmRouteSelectionSource,
} from '../llm-routing';

// Import types from the canonical types file
import type {
  Email,
  PreprocessedEmail,
  ModelConfig,
  Classification,
  ModelResults as BaseModelResults,
  IABSection,
  EvidenceType,
  LLMProvider,
} from './types';

// Worker types for runSummarization
import type {
  SummarizeRequest,
  SummarizeResultMessage,
  ProgressMessage as WorkerProgressMessage,
  ErrorMessage,
  RouteTelemetryMessage,
  WorkerResponse,
} from '../../workers/classification.worker';

// =============================================================================
// RE-EXPORT TYPES (for backward compatibility)
// =============================================================================

export type { Email, PreprocessedEmail, ModelConfig, IABSection, EvidenceType, LLMProvider };

/**
 * Rich classification with full taxonomy and section info
 * Extends Classification with additional fields from iab-classifier
 *
 * Note: taxonomyId is always converted to string for compatibility with base Classification type
 */
export interface RichClassification extends Classification {
  value: string;
  emailIds: string[];
  purchaseIntentFlag?: string;
  categoryPath?: string;
}

/**
 * Timing information
 */
export interface TimingInfo {
  startTime: string;
  endTime: string;
  durationMs: number;
}

/**
 * Cost tracking data
 */
export interface CostData {
  totalCost: number;
  promptTokens: number;
  completionTokens: number;
  inputTokens?: number;
  outputTokens?: number;
}

export interface RouteLevelMetric {
  routeId: string;
  provider: string;
  model: string;
  selectionSource: LlmRouteSelectionSource;
  calls: number;
  successes: number;
  errors: number;
  totalLatencyMs: number;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  costKnownCalls: number;
  validationStatuses: Record<string, number>;
}

export interface RouteBalancedMetrics {
  routeCount: number;
  routesObserved: string[];
  totalCalls: number;
  successRate: number;
  avgLatencyMs: number;
  avgCallsPerRoute: number;
}

export interface JudgeAblationMetrics {
  judgeRouteId: typeof IAB_EVIDENCE_JUDGE_ROUTE_ID;
  analyzerRouteIds: string[];
  judgeCalls: number;
  analyzerCalls: number;
  judgeLatencyMs: number;
  analyzerLatencyMs: number;
  // Gate 04 invariant: analyzer-only runs are debug/ablation only, never product mode.
  analyzerOnlyMode: false;
  // Gate 04 invariant: the shipped classification suite includes the evidence judge.
  productValid: true;
}

/**
 * Model statistics (extended)
 */
export interface ModelStats {
  avgConfidence: number;
  minConfidence: number;
  maxConfidence: number;
  totalClassifications: number;
  uniqueCategories: string[];
  avgEvidenceQuality?: number;
  evidenceTypeDistribution?: Record<EvidenceType, number>;
}

/**
 * Model classification result (extended with section breakdown)
 */
export interface ModelResults extends Omit<BaseModelResults, 'classifications' | 'stats'> {
  classifications: RichClassification[];
  stats: ModelStats;
  timing?: TimingInfo;
  sectionBreakdown?: {
    demographics: RichClassification[];
    household: RichClassification[];
    interests: RichClassification[];
    purchase_intent: RichClassification[];
  };
  costData?: CostData;
  classificationSuite?: {
    mode: 'full_judged_suite';
    routeIds: string[];
    // Gate 04 invariant: Stage 3 always runs the judged five-route suite.
    analyzerOnlyMode: false;
    productValid: true;
  };
  routeTelemetry?: LlmRouteTelemetryEvent[];
  routeMetrics?: Record<string, RouteLevelMetric>;
  routeBalancedMetrics?: RouteBalancedMetrics;
  judgeAblationMetrics?: JudgeAblationMetrics;
}

/**
 * Progress callback type
 */
export type ProgressCallback = (
  modelKey: string,
  status: 'started' | 'completed' | 'error',
  message?: string
) => void;

// =============================================================================
// WORKER SINGLETON (from parallel-classify.ts)
// =============================================================================

let classificationWorker: Worker | null = null;

function getWorker(): Worker {
  if (!classificationWorker) {
    classificationWorker = new Worker(
      new URL('../../workers/classification.worker.ts', import.meta.url),
      { type: 'module' }
    );
  }
  return classificationWorker;
}

/**
 * Clean up worker when done
 */
export function terminateWorker(): void {
  if (classificationWorker) {
    classificationWorker.terminate();
    classificationWorker = null;
    console.log(`[IAB Classifier Adapter] Worker terminated`);
  }
}

// =============================================================================
// SHARED UTILITIES
// =============================================================================

/**
 * Extended TaxonomySelection type that includes email_ids from analyzer results
 */
interface AnalyzerTaxonomySelection extends TaxonomySelection {
  email_ids?: string[];
  evidence_quality?: number;
  evidence_type?: EvidenceType;
}

async function getBrowserPrivyAccessToken(): Promise<string | null> {
  const { getAccessToken } = await import('@privy-io/react-auth');
  return getAccessToken();
}

/**
 * Map TaxonomySelection to RichClassification
 */
function mapToRichClassification(
  selection: AnalyzerTaxonomySelection,
  section: IABSection
): RichClassification {
  return {
    emailId: selection.email_ids?.[0] || '',
    section,
    // Convert taxonomyId to string for type compatibility
    taxonomyId: String(selection.taxonomy_id),
    value: selection.value,
    confidence: selection.confidence,
    reasoning: selection.reasoning || '',
    categoryPath: selection.category_path,
    tier1: selection.tier_1,
    tier2: selection.tier_2,
    tier3: selection.tier_3,
    tier4: selection.tier_4,
    tier5: selection.tier_5,
    emailIds: selection.email_ids || [],
    purchaseIntentFlag: selection.purchase_intent_flag,
    evidenceQuality: selection.evidence_quality,
    evidenceType: selection.evidence_type,
    // For compatibility
    category: selection.value,
    tierPath: [selection.tier_1, selection.tier_2, selection.tier_3, selection.tier_4, selection.tier_5]
      .filter(Boolean)
      .join(' > '),
  };
}

function createEmptyRouteMetric(event: LlmRouteTelemetryEvent): RouteLevelMetric {
  return {
    routeId: event.routeId,
    provider: event.provider ?? 'unknown',
    model: event.model ?? 'unknown',
    selectionSource: event.selectionSource ?? 'experiment',
    calls: 0,
    successes: 0,
    errors: 0,
    totalLatencyMs: 0,
    inputTokens: 0,
    outputTokens: 0,
    totalTokens: 0,
    costKnownCalls: 0,
    validationStatuses: {},
  };
}

function addRouteMetricEvent(
  metrics: Record<string, RouteLevelMetric>,
  event: LlmRouteTelemetryEvent,
): void {
  const metric = metrics[event.routeId] ?? createEmptyRouteMetric(event);
  metric.calls += 1;
  metric.successes += event.result === 'success' ? 1 : 0;
  metric.errors += event.result === 'error' ? 1 : 0;
  metric.totalLatencyMs += event.latencyMs;
  metric.inputTokens += event.usage?.inputTokens ?? 0;
  metric.outputTokens += event.usage?.outputTokens ?? 0;
  metric.totalTokens += event.usage?.totalTokens ?? 0;
  metric.costKnownCalls += event.costKnown ? 1 : 0;
  const validationStatus = event.validation.status;
  metric.validationStatuses[validationStatus] = (metric.validationStatuses[validationStatus] ?? 0) + 1;
  metrics[event.routeId] = metric;
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function computeRouteBalancedMetrics(
  metrics: Record<string, RouteLevelMetric>,
): RouteBalancedMetrics {
  const routeMetrics = Object.values(metrics);
  const totalCalls = routeMetrics.reduce((sum, metric) => sum + metric.calls, 0);
  const successes = routeMetrics.reduce((sum, metric) => sum + metric.successes, 0);
  const latency = routeMetrics.reduce((sum, metric) => sum + metric.totalLatencyMs, 0);

  return {
    routeCount: routeMetrics.length,
    routesObserved: routeMetrics.map((metric) => metric.routeId),
    totalCalls,
    successRate: totalCalls > 0 ? successes / totalCalls : 0,
    avgLatencyMs: totalCalls > 0 ? latency / totalCalls : 0,
    avgCallsPerRoute: routeMetrics.length > 0 ? totalCalls / routeMetrics.length : 0,
  };
}

function computeJudgeAblationMetrics(
  metrics: Record<string, RouteLevelMetric>,
): JudgeAblationMetrics {
  // Gate 04 invariant: IAB_CLASSIFICATION_ROUTE_IDS includes the evidence judge
  // as the fifth full-suite experiment route.
  const judge = metrics[IAB_EVIDENCE_JUDGE_ROUTE_ID];
  const analyzerRouteIds = IAB_CLASSIFICATION_ROUTE_IDS
    .filter((routeId) => routeId !== IAB_EVIDENCE_JUDGE_ROUTE_ID);
  const analyzerMetrics = analyzerRouteIds
    .map((routeId) => metrics[routeId])
    .filter((metric): metric is RouteLevelMetric => Boolean(metric));

  return {
    judgeRouteId: IAB_EVIDENCE_JUDGE_ROUTE_ID,
    analyzerRouteIds: [...analyzerRouteIds],
    judgeCalls: judge?.calls ?? 0,
    analyzerCalls: analyzerMetrics.reduce((sum, metric) => sum + metric.calls, 0),
    judgeLatencyMs: judge?.totalLatencyMs ?? 0,
    analyzerLatencyMs: analyzerMetrics.reduce((sum, metric) => sum + metric.totalLatencyMs, 0),
    analyzerOnlyMode: false,
    productValid: true,
  };
}

/**
 * Compute model statistics from classifications
 */
function computeStats(classifications: RichClassification[]): ModelStats {
  if (classifications.length === 0) {
    return {
      avgConfidence: 0,
      minConfidence: 0,
      maxConfidence: 0,
      totalClassifications: 0,
      uniqueCategories: [],
      avgEvidenceQuality: 0,
      evidenceTypeDistribution: {
        explicit: 0,
        contextual: 0,
        weak: 0,
        inappropriate: 0,
      },
    };
  }

  const confidences = classifications.map(c => c.confidence);
  const categories = [...new Set(classifications.map(c => c.value))];

  // Evidence quality stats
  const evidenceQualities = classifications
    .filter(c => c.evidenceQuality !== undefined)
    .map(c => c.evidenceQuality!);

  const evidenceTypeDistribution: Record<EvidenceType, number> = {
    explicit: 0,
    contextual: 0,
    weak: 0,
    inappropriate: 0,
  };
  for (const c of classifications) {
    if (c.evidenceType && c.evidenceType in evidenceTypeDistribution) {
      evidenceTypeDistribution[c.evidenceType]++;
    }
  }

  return {
    avgConfidence: confidences.reduce((a, b) => a + b, 0) / confidences.length,
    minConfidence: Math.min(...confidences),
    maxConfidence: Math.max(...confidences),
    totalClassifications: classifications.length,
    uniqueCategories: categories,
    avgEvidenceQuality: evidenceQualities.length > 0
      ? evidenceQualities.reduce((a, b) => a + b, 0) / evidenceQualities.length
      : 0,
    evidenceTypeDistribution,
  };
}

// =============================================================================
// CORE CLASSIFICATION (Sprint 11e with CostTracker from Sprint 11d)
// =============================================================================

/**
 * Classify emails with full iab-classifier workflow
 *
 * Sprint 11e Pre-Integration Fix 0B: Implements batching loop
 *
 * CRITICAL: The iab-classifier processes ONE batch per invocation.
 * This function loops until all emails are processed.
 *
 * @param emails - Emails to classify
 * @param model - Model configuration
 * @param userId - User ID for classification
 * @param store - LangGraph store adapter
 * @param onProgress - Optional progress callback
 * @returns Model results with all classifications
 */
export async function classifyWithFullWorkflowBatched(
  emails: PreprocessedEmail[],
  model: ModelConfig,
  userId: string,
  store: LangGraphStoreAdapter,
  onProgress?: (status: string) => void,
  routeAuthToken?: string | null,
  onRouteTelemetry?: (event: LlmRouteTelemetryEvent) => void,
): Promise<ModelResults> {
  const startTime = new Date().toISOString();
  const modelKey = `${model.provider}:${model.model}`;

  // Handle empty email list
  if (emails.length === 0) {
    return {
      modelKey,
      classifications: [],
      stats: computeStats([]),
      sectionBreakdown: {
        demographics: [],
        household: [],
        interests: [],
        purchase_intent: [],
      },
    };
  }

  // Create cost tracker for this model run (from Sprint 11d)
  const costTracker = new CostTracker();
  const routeTelemetry: LlmRouteTelemetryEvent[] = [];
  const routeMetrics: Record<string, RouteLevelMetric> = {};

  const routeClientFactory: IABRouteClientFactory = ({ routeId }) => new RouteBackedAnalyzerLLMClient({
    routeId,
    provider: model.provider,
    model: model.model,
    cost_tracker: costTracker,
    executeRoute: async ({ routeId: executionRouteId, messages, temperature, maxTokens }) => {
      const result = await executeLlmTextRoute({
        routeId: executionRouteId,
        selection: {
          provider: model.provider,
          model: model.model,
        },
        selectionSource: 'experiment',
        messages,
        temperature,
        maxTokens,
        authToken: routeAuthToken,
        emitTelemetry: (event) => {
          routeTelemetry.push(event);
          addRouteMetricEvent(routeMetrics, event);
          onRouteTelemetry?.(event);
        },
      });

      return {
        content: result.text,
        provider: result.provider,
        model: result.model,
        usage: result.usage,
      };
    },
  });

  // Create the classifier
  const classifier = createIABClassifier({
    store: store as any, // BaseStore compatible via adapter
    llm_route_client_factory: routeClientFactory,
  });

  // Get model's context window from ConfigService for batch sizing
  let contextWindow = 128000; // Default 128k
  try {
    const modelMetadata = await configService.getModelMetadata(model.model);
    if (modelMetadata?.contextWindow) {
      contextWindow = modelMetadata.contextWindow;
    }
  } catch (error) {
    console.warn(`[IAB Classifier Adapter] Could not get model metadata, using default context window: ${contextWindow}`);
  }

  const allClassifications: RichClassification[] = [];
  const batchErrors: string[] = [];
  const sectionBreakdown = {
    demographics: [] as RichClassification[],
    household: [] as RichClassification[],
    interests: [] as RichClassification[],
    purchase_intent: [] as RichClassification[],
  };

  let processedCount = 0;
  const totalEmails = emails.length;

  // Process in batches until all emails are done (Fix 0B)
  while (processedCount < totalEmails) {
    const remainingEmails = emails.slice(processedCount);

    onProgress?.(`Processing batch starting at email ${processedCount + 1}/${totalEmails}`);

    try {
      const result = await classifier.invoke({
        userId,
        text: remainingEmails.map(e => `Subject: ${e.subject}\nFrom: ${e.from}\n${e.summary}`).join('\n\n'),
        source: 'ab-testing',
        sourceItemId: remainingEmails[0].id,
        emails: remainingEmails.map(e => ({
          id: e.id,
          subject: e.subject,
          from: e.from,
          summary: e.summary,
        })),
        force_reprocess: true, // FIX Bug 1: A/B testing ALWAYS reprocesses to compare models on same emails
        model_context_window: contextWindow, // CRITICAL: Enables dynamic batch sizing
        cost_tracker: costTracker, // CostTracker integration from Sprint 11d
      });

      // Handle both old format (direct result) and new format (wrapped)
      const workflowResult = result.classification ? result : result;

      // Extract classifications from result (demographics_results, household_results, etc.)
      const demographicsResults = (workflowResult as any).demographics_results || [];
      const householdResults = (workflowResult as any).household_results || [];
      const interestsResults = (workflowResult as any).interests_results || [];
      const purchaseResults = (workflowResult as any).purchase_results || [];

      // Map to RichClassification and track sections
      for (const sel of demographicsResults) {
        const rich = mapToRichClassification(sel, 'demographics');
        allClassifications.push(rich);
        sectionBreakdown.demographics.push(rich);
      }
      for (const sel of householdResults) {
        const rich = mapToRichClassification(sel, 'household');
        allClassifications.push(rich);
        sectionBreakdown.household.push(rich);
      }
      for (const sel of interestsResults) {
        const rich = mapToRichClassification(sel, 'interests');
        allClassifications.push(rich);
        sectionBreakdown.interests.push(rich);
      }
      for (const sel of purchaseResults) {
        const rich = mapToRichClassification(sel, 'purchase_intent');
        allClassifications.push(rich);
        sectionBreakdown.purchase_intent.push(rich);
      }

      // Determine how many emails were processed in this batch
      const processedEmailIds = new Set<string>();
      const batchClassifications = [
        ...demographicsResults,
        ...householdResults,
        ...interestsResults,
        ...purchaseResults,
      ];
      for (const c of batchClassifications) {
        for (const emailId of c.email_ids || []) {
          processedEmailIds.add(emailId);
        }
      }

      // If no emails were processed, advance by at least 1 to prevent infinite loop
      const batchProcessed = processedEmailIds.size > 0 ? processedEmailIds.size : 1;
      processedCount += batchProcessed;

      console.log(`[IAB Classifier Adapter] Batch complete: processed ${batchProcessed} emails, total ${processedCount}/${totalEmails}`);
    } catch (error) {
      const message = getErrorMessage(error);
      console.error(`[IAB Classifier Adapter] Batch failed:`, error);
      batchErrors.push(message);
      // Advance by at least 1 to prevent infinite loop on repeated errors
      processedCount += 1;
    }
  }

  const endTime = new Date().toISOString();

  if (allClassifications.length === 0 && batchErrors.length > 0) {
    throw new Error(`Classification failed for all batches: ${batchErrors[0]}`);
  }

  // ===== FINAL SUMMARY =====
  console.log(`\n🏁 ═══════════════════════════════════════════════════════════`);
  console.log(`🏁 MODEL CLASSIFICATION COMPLETE: ${model.provider}/${model.model}`);
  console.log(`🏁 ═══════════════════════════════════════════════════════════`);
  console.log(`🏁 Emails processed:    ${totalEmails}`);
  console.log(`🏁 Demographics:        ${sectionBreakdown.demographics.length}`);
  console.log(`🏁 Household:           ${sectionBreakdown.household.length}`);
  console.log(`🏁 Interests:           ${sectionBreakdown.interests.length}`);
  console.log(`🏁 Purchase Intent:     ${sectionBreakdown.purchase_intent.length}`);
  console.log(`🏁 ───────────────────────────────────────────────────────────`);
  console.log(`🏁 TOTAL CLASSIFICATIONS: ${allClassifications.length}`);
  console.log(`🏁 Duration: ${((new Date(endTime).getTime() - new Date(startTime).getTime()) / 1000).toFixed(1)}s`);
  console.log(`🏁 ═══════════════════════════════════════════════════════════\n`);

  // Get cost data from tracker (Sprint 11d integration)
  let costData: CostData | undefined;
  try {
    const stats = costTracker.getStatsDict();
    costData = {
      totalCost: stats.total_cost_usd as number || 0,
      promptTokens: Object.values(stats.providers as Record<string, { prompt_tokens: number }>).reduce(
        (sum, p) => sum + (p.prompt_tokens || 0),
        0
      ),
      completionTokens: Object.values(stats.providers as Record<string, { completion_tokens: number }>).reduce(
        (sum, p) => sum + (p.completion_tokens || 0),
        0
      ),
    };
  } catch {
    // CostTracker may not have data
  }

  return {
    modelKey,
    classifications: allClassifications,
    stats: computeStats(allClassifications),
    timing: {
      startTime,
      endTime,
      durationMs: new Date(endTime).getTime() - new Date(startTime).getTime(),
    },
    processingTime: new Date(endTime).getTime() - new Date(startTime).getTime(),
    sectionBreakdown,
    costData,
    errors: batchErrors.length > 0 ? batchErrors : undefined,
    // Map to base ModelResults.cost format (requires inputTokens/outputTokens as numbers)
    cost: costData ? {
      inputTokens: costData.promptTokens,
      outputTokens: costData.completionTokens,
      totalCost: costData.totalCost,
    } : undefined,
    classificationSuite: {
      mode: 'full_judged_suite',
      routeIds: [...IAB_CLASSIFICATION_ROUTE_IDS],
      analyzerOnlyMode: false,
      productValid: true,
    },
    routeTelemetry,
    routeMetrics,
    routeBalancedMetrics: computeRouteBalancedMetrics(routeMetrics),
    judgeAblationMetrics: computeJudgeAblationMetrics(routeMetrics),
  };
}

// =============================================================================
// MAIN ENTRY POINTS
// =============================================================================

/**
 * Run classification using the full @ownyou/iab-classifier workflow.
 *
 * Sequential execution ensures:
 * - Same store state for each model (fair comparison)
 * - Production-like behavior
 * - Accurate cost tracking per model
 *
 * @param preprocessedEmails - Emails to classify
 * @param selectedModels - Models to compare
 * @param userId - User ID for profile storage
 * @param store - BaseStore instance from StoreContext
 * @param onProgress - Optional progress callback
 * @returns Map of model key to ModelResults
 */
export async function runClassification(
  preprocessedEmails: PreprocessedEmail[],
  selectedModels: ModelConfig[],
  userId: string,
  store: BaseStore | null,
  onProgress?: ProgressCallback
): Promise<Map<string, ModelResults>> {
  console.log(`[IAB Classifier Adapter] ===== Starting Classification =====`);
  console.log(`[IAB Classifier Adapter] Emails: ${preprocessedEmails.length}`);
  console.log(`[IAB Classifier Adapter] Models: ${selectedModels.map(m => `${m.provider}:${m.model}`).join(', ')}`);

  if (!store) {
    throw new Error('Store is required for classification. Please ensure StoreContext is available.');
  }

  const results = new Map<string, ModelResults>();

  // Run models SEQUENTIALLY for fair comparison
  for (const model of selectedModels) {
    const modelKey = `${model.provider}:${model.model}`;
    // FIX Bug 4: Use unique userId per model to prevent shared state contamination
    // This ensures each model has its own isolated classification context
    const modelUserId = `${userId}_${model.provider}_${model.model}`;
    onProgress?.(modelKey, 'started', `Starting ${model.displayName || model.model}...`);

    try {
      const needsRouteAuth = isLlmRouteCloudProvider(model.provider);
      const routeAuthToken = needsRouteAuth ? await getBrowserPrivyAccessToken() : null;
      if (needsRouteAuth && !routeAuthToken) {
        throw new Error(`Sign in again to use server-side ${model.provider}`);
      }

      const result = await classifyWithFullWorkflowBatched(
        preprocessedEmails,
        model,
        modelUserId,
        store as unknown as LangGraphStoreAdapter,
        (status) => onProgress?.(modelKey, 'started', status),
        routeAuthToken,
      );

      onProgress?.(modelKey, 'completed', `${model.displayName || model.model} complete`);
      results.set(modelKey, result);

      console.log(
        `[IAB Classifier Adapter] Model ${modelKey}: ${result.stats.totalClassifications} classifications, ` +
          `avg confidence: ${(result.stats.avgConfidence * 100).toFixed(1)}%`
      );

      if (result.costData) {
        console.log(
          `[IAB Classifier Adapter] Model ${modelKey}: Cost $${result.costData.totalCost.toFixed(4)} ` +
            `(${result.costData.promptTokens} in / ${result.costData.completionTokens} out tokens)`
        );
      }
    } catch (error) {
      console.error(`[IAB Classifier Adapter] Model ${modelKey} failed:`, error);
      onProgress?.(modelKey, 'error', String(error));

      // Store error result instead of failing all models
      results.set(modelKey, {
        modelKey,
        classifications: [],
        stats: computeStats([]),
        errors: [String(error)],
      });
    }
  }

  const modelResults = Array.from(results.values());
  const allModelsFailed = modelResults.length > 0
    && modelResults.every((result) => result.classifications.length === 0 && (result.errors?.length ?? 0) > 0);
  if (allModelsFailed) {
    const firstError = modelResults.flatMap((result) => result.errors ?? [])[0] ?? 'unknown error';
    throw new Error(`Classification failed for all models: ${firstError}`);
  }

  console.log(`[IAB Classifier Adapter] ===== Classification Complete =====`);
  return results;
}

// =============================================================================
// SUMMARIZATION (from parallel-classify.ts - PRESERVED AS-IS)
// =============================================================================

/**
 * Run LLM summarization for Stage 2 pre-processing
 *
 * Uses Web Worker for direct LLM calls - NO API routes, NO fallback.
 * If summarization fails, the error propagates (no substring fallback).
 */
export async function runSummarization(
  emails: Array<{ id: string; subject: string; from: string; body: string; date?: string }>,
  provider: string,
  model: string,
  _userId: string
): Promise<PreprocessedEmail[]> {
  console.log(`[IAB Classifier Adapter] ===== Starting LLM Pre-processing (Worker) =====`);
  console.log(`[IAB Classifier Adapter] Emails: ${emails.length}`);
  console.log(`[IAB Classifier Adapter] Summarizer model: ${provider}:${model}`);

  const needsRouteAuth = isLlmRouteCloudProvider(provider);
  const authToken = needsRouteAuth ? await getBrowserPrivyAccessToken() : null;
  if (needsRouteAuth && !authToken) {
    throw new Error(`Sign in again to use server-side ${provider}`);
  }

  return new Promise((resolve, reject) => {
    const worker = getWorker();
    const requestId = `summarize-${Date.now()}`;

    const handleMessage = (event: MessageEvent<WorkerResponse>) => {
      const message = event.data;

      if (message.requestId !== requestId) return;

      if (message.type === 'progress') {
        const progress = message as WorkerProgressMessage;
        console.log(`[IAB Classifier Adapter] Summarization progress: ${progress.completed}/${progress.total}`);
      }

      if (message.type === 'route_telemetry') {
        const telemetry = message as RouteTelemetryMessage;
        console.log(`[IAB Classifier Adapter] Route telemetry:`, telemetry.event);
      }

      if (message.type === 'summarize_result') {
        const resultMsg = message as SummarizeResultMessage;
        console.log(`[IAB Classifier Adapter] ===== LLM Pre-processing Complete =====`);
        console.log(`[IAB Classifier Adapter] Processed ${resultMsg.emails.length} emails via worker`);

        worker.removeEventListener('message', handleMessage);
        resolve(resultMsg.emails);
      }

      if (message.type === 'error') {
        const errorMsg = message as ErrorMessage;
        console.error(`[IAB Classifier Adapter] Summarization error:`, errorMsg.error);
        worker.removeEventListener('message', handleMessage);
        // NO FALLBACK - propagate the error
        reject(new Error(`Summarization failed: ${errorMsg.error}`));
      }
    };

    worker.addEventListener('message', handleMessage);

    // Map provider name to LLMProvider type
    const providerMap: Record<string, string> = {
      openai: 'openai',
      anthropic: 'claude',
      google: 'gemini',
      groq: 'groq',
      deepinfra: 'deepinfra',
      xai: 'xai',
      grok: 'xai',
      webllm: 'webllm',
      ollama: 'ollama',
    };

    // Send summarize request to worker
    const mappedProvider = (providerMap[provider] || provider) as LLMProvider;
    const request: SummarizeRequest = {
      type: 'summarize',
      requestId,
      emails: emails.map(e => ({
        id: e.id,
        subject: e.subject,
        from: e.from,
        body: e.body,
        date: e.date || new Date().toISOString(),
      })) as Email[],
      model: {
        provider: mappedProvider,
        model,
        displayName: model,
      },
      routeId: IAB_INPUT_PREPROCESSING_ROUTE_ID,
      selectionSource: 'experiment',
      authToken: authToken ?? undefined,
    };

    console.log(`[IAB Classifier Adapter] Sending summarize request to worker`);
    worker.postMessage(request);
  });
}

/**
 * Check if full classifier is available (store context required)
 */
export function isFullClassifierAvailable(store: BaseStore | null): boolean {
  return store !== null;
}
